import React, {useState, useEffect, useRef} from 'react';
import {useSelector, useDispatch} from "react-redux";
import {Modal} from "react-bootstrap";
import {toast} from "react-toastify";
import {getObjectFromArrayOfObjects} from "../../../../_helpers/helperFunctions";
import SearchTable from "../../../primer/Search-Table";
import {
    postSubmittedF5Declarations,
    fetchSubmittedF5Declarations,
    fetchAccountingSettings
} from "../../../../_apis/api";
import {setTaxisScrapingState} from "../../../../_reducers/DataPanel/Accounting/accountingSlice";

const EXTENSION_STORE_URL = "https://chromewebstore.google.com/detail/inimchhmmahoignojckagbdbjhigglce";

const DEFAULT_TAXIS_CREDENTIALS = {
    username: "",
    password: "",
    isAccountant: false,
    accountantAfm: ""
};

const monthOptions = [
    {value: "1", label: "Ιανουάριος"},
    {value: "2", label: "Φεβρουάριος"},
    {value: "3", label: "Μάρτιος"},
    {value: "4", label: "Απρίλιος"},
    {value: "5", label: "Μάιος"},
    {value: "6", label: "Ιούνιος"},
    {value: "7", label: "Ιούλιος"},
    {value: "8", label: "Αύγουστος"},
    {value: "9", label: "Σεπτέμβριος"},
    {value: "10", label: "Οκτώβριος"},
    {value: "11", label: "Νοέμβριος"},
    {value: "12", label: "Δεκέμβριος"}
];

const declarationTypeOptions = [
    {value: "INITIAL", label: "Αρχική"},
    {value: "AMENDING", label: "Διορθωτική / Τροποποιητική"}
];

const searchF5Filters = {
    filters: [
        {name: "month", label: "Μήνας", type: "multiselect", options: monthOptions, mdWidth: 3},
        {
            name: "declarationType",
            label: "Είδος Δήλωσης",
            type: "multiselect",
            options: declarationTypeOptions,
            mdWidth: 3
        },
        {name: "total_bc5_from", label: "Σύνολο Αποκτήσεων (5) Από (€)", type: "input", mdWidth: 3},
        {name: "total_bc5_to", label: "Σύνολο Αποκτήσεων (5) Έως (€)", type: "input", mdWidth: 3},
        {name: "total_bc6_from", label: "Σύνολο Τριγωνικών (6) Από (€)", type: "input", mdWidth: 3},
        {name: "total_bc6_to", label: "Σύνολο Τριγωνικών (6) Έως (€)", type: "input", mdWidth: 3},
        {name: "total_bc7_from", label: "Σύνολο Υπηρεσιών (7) Από (€)", type: "input", mdWidth: 3},
        {name: "total_bc7_to", label: "Σύνολο Υπηρεσιών (7) Έως (€)", type: "input", mdWidth: 3}
    ]
};

const f5Columns = [
    { name: "Ημερομηνία Υποβολής", field: "submissionDate", width: "16%", editable: false, inputType: { type: 'text' } },
    { name: "Μήνας", field: "monthName", width: "14%", editable: false, inputType: { type: 'text' } },
    { name: "Είδος Δήλωσης", field: "declarationTypeName", width: "16%", editable: false, inputType: { type: 'text' } },
    {
        name: "Αποκτήσεις Αγαθών (5)",
        field: "totalBc5",
        width: "18%",
        editable: false,
        inputType: { type: 'accounting', config: { suffix: "€" } }
    },
    {
        name: "Τριγωνικές (6)",
        field: "totalBc6",
        width: "18%",
        editable: false,
        inputType: { type: 'accounting', config: { suffix: "€" } }
    },
    {
        name: "Λήψεις Υπηρεσιών (7)",
        field: "totalBc7",
        width: "18%",
        editable: false,
        inputType: { type: 'accounting', config: { suffix: "€" } }
    }
];

const checkExtensionInstalled = (timeoutMs = 800) => {
    return new Promise((resolve) => {
        const handleMessage = (event) => {
            if (event.source === window && event.data?.type === 'TAXIS_EXTENSION_PONG') {
                window.removeEventListener('message', handleMessage);
                clearTimeout(timer);
                resolve(true);
            }
        };
        window.addEventListener('message', handleMessage);
        window.postMessage({type: 'PING_TAXIS_EXTENSION'}, '*');
        const timer = setTimeout(() => {
            window.removeEventListener('message', handleMessage);
            resolve(false);
        }, timeoutMs);
    });
};

const isChromiumBrowser = () => {
    const nav = window.navigator;
    if (nav.userAgentData && Array.isArray(nav.userAgentData.brands)) {
        return nav.userAgentData.brands.some((b) =>
            ['Chromium', 'Google Chrome', 'Microsoft Edge', 'Opera', 'Brave'].includes(b.brand)
        );
    }
    const ua = nav.userAgent;
    return /Chrome|Chromium|Edg|OPR/i.test(ua) && !(/Firefox|FxiOS|Safari/i.test(ua) && !/Chrome|Edg|OPR/i.test(ua));
};

const SearchSubmittedF5Declarations = () => {
    const dispatch = useDispatch();
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const taxisCredentials = useSelector((state) => state.ACCOUNTING_DATA.taxisCredentials) || DEFAULT_TAXIS_CREDENTIALS;
    const currentTableFilters = useSelector((state) => state.SEARCH_TABLE.filters["Submitted F5 Declarations Search"]) || {};
    const [isScraping, setIsScraping] = useState(false);
    const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
    const [showExtensionModal, setShowExtensionModal] = useState(false);
    const [tableColumns, setTableColumns] = useState(f5Columns.slice());

    const activeScrapeCompanyIdRef = useRef(null);
    const activeScrapeAfmRef = useRef(null);

    const resetScrapeState = () => {
        activeScrapeCompanyIdRef.current = null;
        activeScrapeAfmRef.current = null;
        setIsScraping(false);
        dispatch(setTaxisScrapingState({active: false, message: ""}));
    };

    useEffect(() => {
        if (!company?.id) return;
        dispatch(fetchAccountingSettings({
            company: company.id
        }));
    }, [company?.id, dispatch]);

    useEffect(() => {
        const checkExtension = (event) => {
            if (event.source !== window) return;
            if (event.data?.type === 'TAXIS_EXTENSION_READY' || event.data?.type === 'TAXIS_EXTENSION_PONG') {
                setIsExtensionInstalled(true);
            }
        };

        window.addEventListener('message', checkExtension);
        window.postMessage({type: 'PING_TAXIS_EXTENSION'}, '*');
        return () => window.removeEventListener('message', checkExtension);
    }, []);

    useEffect(() => {
        const handleExtensionMessages = (event) => {
            if (event.source !== window || !event.data) return;
            const {type, action} = event.data;
            const success = event.data.success ?? event.data.response?.success;
            const rawError = event.data.error || event.data.response?.error;
            const scrapedData = event.data.data || event.data.response?.data;

            if (type === 'TO_ERP_AUTOFILL_VAT_RESPONSE' && (success === false || rawError)) {
                resetScrapeState();
                toast.error(rawError || 'Αποτυχία επικοινωνίας με το Taxis Bridge.', {
                    autoClose: 10000
                });
                return;
            }

            if (type === 'TO_ERP_SCRAPE_PROGRESS') {
                toast.info(event.data.message || 'Σε εξέλιξη άντληση δηλώσεων Φ5...', {
                    autoClose: 2500,
                    hideProgressBar: true
                });
                return;
            }

            if (type === 'TO_ERP_SCRAPE_VAT_RESPONSE') {
                if (activeScrapeCompanyIdRef.current && activeScrapeCompanyIdRef.current !== company?.id) {
                    resetScrapeState();
                    return;
                }

                if (success === false || rawError) {
                    resetScrapeState();
                    toast.error(rawError || 'Σφάλμα επικοινωνίας με το Taxisnet κατά την άντληση Φ5.', {
                        autoClose: 10000,
                        pauseOnHover: true
                    });
                    return;
                }

                if (success && (!scrapedData || (Array.isArray(scrapedData) && scrapedData.length === 0))) {
                    resetScrapeState();
                    toast.info('Δεν βρέθηκαν υποβληθείσες δηλώσεις Φ5 στο Taxisnet για το επιλεγμένο έτος.');
                    return;
                }

                if (success && scrapedData) {
                    const declarationsArray = Array.isArray(scrapedData) ? scrapedData : [scrapedData];
                    const scrapedAfm = declarationsArray[0]?.companyAfm?.trim();
                    const currentCompanyAfm = (company?.vatNumber || '').trim();

                    if (scrapedAfm && currentCompanyAfm && scrapedAfm !== currentCompanyAfm) {
                        resetScrapeState();
                        return;
                    }

                    toast.info(`Αντλήθηκαν ${declarationsArray.length} δηλώσεις Φ5. Αποθήκευση στη βάση...`, {
                        autoClose: 4000
                    });

                    const targetCompanyId = company?.id;
                    dispatch(postSubmittedF5Declarations(
                        {company: targetCompanyId, declarations: declarationsArray},
                        setIsScraping,
                        () => {
                            resetScrapeState();
                            toast.success(`Ολοκληρώθηκε επιτυχώς η αποθήκευση ${declarationsArray.length} δηλώσεων Φ5!`);
                            dispatch(fetchSubmittedF5Declarations({
                                company: targetCompanyId,
                                year: String(company?.year || new Date().getFullYear()),
                                ...currentTableFilters
                            }));
                        }
                    ));
                }
                return;
            }

            if (action === 'AUTOFILL_FAILED' || action === 'AUTOFILL_FAILED_AND_CLOSE') {
                resetScrapeState();
                toast.error(rawError || 'Η διαδικασία στο Taxisnet απέτυχε.', {
                    autoClose: 10000,
                    pauseOnHover: true
                });
            }

            if (type === 'TO_ERP_TAXIS_WINDOW_CLOSED') {
                resetScrapeState();
                toast.info('Η διαδικασία myAADE ακυρώθηκε (το παράθυρο έκλεισε).', {
                    autoClose: 4000
                });
            }
        };

        window.addEventListener('message', handleExtensionMessages);
        return () => window.removeEventListener('message', handleExtensionMessages);
    }, [company?.id, company?.vatNumber, company?.year, currentTableFilters, dispatch]);

    const handleScrapeAllSubmittedF5 = async () => {
        if (!isChromiumBrowser()) {
            toast.warning("Απαιτείται περιηγητής Chromium (Chrome, Edge, Brave, Opera).", {
                autoClose: 5000,
                pauseOnHover: true
            });
            return;
        }

        let installed = isExtensionInstalled;
        if (!installed) {
            installed = await checkExtensionInstalled(800);
            if (installed) {
                setIsExtensionInstalled(true);
            }
        }

        if (!installed) {
            setShowExtensionModal(true);
            return;
        }

        if (!taxisCredentials?.username || !taxisCredentials?.password) {
            toast.error("Δεν έχουν οριστεί κωδικοί Taxisnet στις ρυθμίσεις.");
            return;
        }

        const resolvedAfm = company?.vatNumber?.trim();
        if (!resolvedAfm) {
            toast.error("Δεν βρέθηκε έγκυρο ΑΦΜ εταιρείας.");
            return;
        }

        try {
            const targetYear = String(company?.year || new Date().getFullYear());

            const payload = {
                credentials: {
                    username: taxisCredentials.username,
                    password: taxisCredentials.password,
                    isAccountant: Boolean(taxisCredentials.isAccountant),
                    accountantAfm: taxisCredentials.accountantAfm
                },
                year: targetYear,
                companyAfm: resolvedAfm,
                actionType: 'SCRAPE_F5_ALL_SUBMITTED'
            };

            activeScrapeCompanyIdRef.current = company.id;
            activeScrapeAfmRef.current = resolvedAfm;
            setIsScraping(true);

            dispatch(setTaxisScrapingState({
                active: true,
                message: `Σε εξέλιξη μαζική άντληση δηλώσεων Φ5 έτους ${targetYear} από το Taxisnet...`
            }));

            toast.info(`Έναρξη πλήρους άντλησης δηλώσεων Φ5 έτους ${targetYear}...`, {
                autoClose: 3000
            });

            window.postMessage({ type: 'FROM_ERP_AUTOFILL_VAT', payload }, '*');

        } catch (err) {
            console.error("Scrape F5 Error:", err);
            resetScrapeState();
            toast.error("Σφάλμα κατά την προετοιμασία της άντλησης.");
        }
    };

    const handleColumnShowHide = (field) => {
        let tempTc = tableColumns.slice();
        let object = getObjectFromArrayOfObjects(tempTc, field, 'field');
        const index = tempTc.findIndex((item) => field === item.field);
        if (object.length > 0) {
            tempTc[index].hiddenColumn = !object['0'].hiddenColumn;
            setTableColumns(tempTc);
        }
    };

    return (
        <div>
            <SearchTable
                tableName="Submitted F5 Declarations Search"
                key="Submitted F5 Declarations Search"
                hideYearFilter={false}
                allowOtherYears={true}
                allowPagination={true}
                allowToggleColumns={true}
                toggleColumns={handleColumnShowHide}
                allowPrintResults={true}
                exportFileName="Υποβληθείσες Δηλώσεις Φ5 VIES"
                dataEndpoint="/accounting/get-submitted-f5-vat-declarations"
                columns={tableColumns}
                data={[]}
                filtersConfig={searchF5Filters}
                onScrapeClick={handleScrapeAllSubmittedF5}
                isScraping={isScraping}
                scrapeButtonTitle="Άντληση Φ5 από Taxisnet"
            />

            <Modal
                show={showExtensionModal}
                onHide={() => setShowExtensionModal(false)}
                dialogClassName={"modal65PercentWidth"}
                centered
            >
                <Modal.Header closeButton className="border-bottom-0 pb-2">
                    <Modal.Title className="d-flex align-items-center">
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{fontSize: "1.05rem", color: "#111827"}}>
                                Απαιτείται το Πρόσθετο Primer ERP - Taxis Bridge
                            </h5>
                            <span className="text-muted" style={{fontSize: "0.75rem"}}>
                                Αυτοματοποίηση διασύνδεσης με το σύστημα Taxisnet
                            </span>
                        </div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-2 pb-3 px-4">
                    <p className="text-muted mb-3" style={{fontSize: "0.85rem", lineHeight: "1.45"}}>
                        Για την ασφαλή άντληση των ανακεφαλαιωτικών πινάκων (Φ4 / Φ5) και των δηλώσεων ΦΠΑ από την ΑΑΔΕ,
                        είναι
                        απαραίτητη η εγκατάσταση και σωστή ρύθμιση του εργαλείου στον περιηγητή σας[cite: 1].
                    </p>

                    <div
                        className="p-3 mb-3 rounded"
                        style={{
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0"
                        }}
                    >
                        <div className="font-weight-bold mb-3 text-dark" style={{fontSize: "0.8rem"}}>
                            Οδηγίες Εγκατάστασης:
                        </div>

                        <div className="d-flex flex-column" style={{gap: "10px"}}>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    1
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Πατήστε το κουμπί <strong>«Μετάβαση στο Chrome Web Store»</strong> παρακάτω.
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    2
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Στη σελίδα που θα ανοίξει, πατήστε <strong>«Προσθήκη στο Chrome»</strong> (Add to Chrome).
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    3
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Ανοίξτε τη διαχείριση επεκτάσεων (πληκτρολογήστε <code
                                    className="px-1 py-0.5 bg-light rounded border text-danger">chrome://extensions</code> στη γραμμή διευθύνσεων ή πατήστε το εικονίδιο παζλ πάνω δεξιά).
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    4
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Εντοπίστε το <strong>Primer ERP - Taxis Bridge</strong> και πατήστε <strong>Λεπτομέρειες (Details)</strong>.
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    5
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Ενεργοποιήστε την επιλογή <strong>«Να επιτρέπεται σε ανώνυμη περιήγηση» (Allow in incognito)</strong>.
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center text-muted" style={{fontSize: "0.75rem"}}>
                        <i className="fa-solid fa-shield-halved text-primary mr-2"></i>
                        <span>Ασφαλές εργαλείο, εγκεκριμένο από το επίσημο Chrome Web Store.</span>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-top-0 pt-0 px-4 pb-3">
                    <a
                        href={EXTENSION_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center"
                        onClick={() => setShowExtensionModal(false)}
                    >
                        <i className="fa-brands fa-chrome mr-2"></i>
                        Μετάβαση στο Chrome Web Store
                    </a>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SearchSubmittedF5Declarations;